import { AD_STYLES_18, AD_ANGLES_18, STYLE_ALIASES, ANGLE_ALIASES, type AdStyle, type AdAngle } from './constants/adStyles';

export interface CreativeAllocation {
  count: number;
  style?: AdStyle;
  angle?: AdAngle;
  /** A requested visual format without a canonical adStyle; retain it verbatim in planning. */
  format?: string;
}

const WORD_COUNTS: Record<string, number> = {one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,twenty:20,thirty:30,forty:40,fifty:50,hundred:100};
const FORMATS = ['reddit-style', 'reddit posts', 'reddit', 'studio shots', 'studio shot', 'studio photography', 'studio product', 'typographic posters', 'typographic poster', 'text-on-image', 'flat lay', 'comparison chart', 'lifestyle shot', 'split screen'];
const escapeRegex=(text:string)=>text.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
function phrasePattern(phrase:string):string {
 return phrase.split(/[\s\-/&]+/).filter(Boolean).map(escapeRegex).join('[\\s\\-/&]*');
}
const candidates: Array<{pattern:RegExp;value:Omit<CreativeAllocation,'count'>;length:number}> = [];
for(const [alias,style] of Object.entries({...Object.fromEntries(AD_STYLES_18.map(style=>[style.toLowerCase(),style])),...STYLE_ALIASES,'before/after':'Before & After' as AdStyle})) {
 candidates.push({pattern:new RegExp('^'+phrasePattern(alias)+'s?(?![a-z])','i'),value:{style},length:alias.length});
}
for(const [alias,angle] of Object.entries({...Object.fromEntries(AD_ANGLES_18.map(angle=>[angle.toLowerCase(),angle])),...ANGLE_ALIASES})) {
 // "dr" is too ambiguous to treat as an explicit allocation.
 if(alias==='dr')continue;
 candidates.push({pattern:new RegExp('^'+phrasePattern(alias)+'s?(?![a-z])','i'),value:{angle},length:alias.length});
}
for(const format of FORMATS)candidates.push({pattern:new RegExp('^'+phrasePattern(format)+'(?![a-z])','i'),value:{format},length:format.length});
candidates.sort((a,b)=>b.length-a.length);

/** Extract only explicit numbered known formats, styles or angles. Unspecified slots remain free.
 * No LLM inference, equal splitting, quota rescaling, or invented canonical styles.
 * Omit totalCount when reading a request whose total is the sum of its named quotas.
 */
export function parseCreativeAllocation(brief:string,totalCount?:number):CreativeAllocation[]|null {
 const rows:CreativeAllocation[]=[];
 const quantities=new RegExp('(?<![\\d.$])(?:\\d{1,6}|\\b'+Object.keys(WORD_COUNTS).join('\\b|\\b')+'\\b)\\s*','gi');
 for(const match of Array.from(brief.matchAll(quantities))) {
  const count=Number(match[0].trim())||WORD_COUNTS[match[0].trim().toLowerCase()];
  if(!Number.isInteger(count)||count<=0)continue;
  const tail=brief.slice(match.index!+match[0].length);
  const found=candidates.find(candidate=>candidate.pattern.test(tail));
  const rawFormat=found ? undefined : /^(?!ads?\b|creatives?\b|images?\b)([a-z][a-z/&-]*(?:\s+[a-z][a-z/&-]*){0,4}\s+(?:ads?|shots?|posters?|splits?|posts?))(?![a-z])/i.exec(tail)?.[1];
  if(!found && (!rawFormat || /\b(?:with|for|in|on|per|each|every|and|of)\b/i.test(rawFormat)))continue;
  const label=found ? found.pattern.exec(tail)![0] : rawFormat!;
  // Numbers of claims/items inside an ad are not numbers of ads.
  if(/^\s*(?:per\b|each\b|in\s+(?:each|every|one|a|the)\b|on\s+(?:each|every|one|a|the)\b)/i.test(tail.slice(label.length)))continue;
  rows.push({count,...(found?.value ?? {format:rawFormat!})});
 }
 if(!rows.length)return null;
 const allocated=rows.reduce((sum,row)=>sum+row.count,0);
 if(totalCount!==undefined && allocated>totalCount)throw new Error(`Requested format quantities (${allocated}) exceed the requested ad count (${totalCount}).`);
 return rows;
}

/** Resolve an explicit overall count first, otherwise sum explicit format clauses.
 * A fallback is only used when the request specifies neither; never cap a requested count.
 */
export function creativeRequestCount(brief:string,fallback=5):number {
 const quantities='(?:\\d{1,6}|'+Object.keys(WORD_COUNTS).join('|')+')';
 const heading=new RegExp('(?<![\\d.$])('+quantities+')\\s*(?:(?:more|new|fresh|additional)\\s+)?(?:ads?(?:\\s+concepts?)?|creatives?|concepts?|images?)(?![a-z])','i').exec(brief);
 const explicit=heading ? Number(heading[1])||WORD_COUNTS[heading[1].toLowerCase()] : undefined;
 const rows=parseCreativeAllocation(brief,explicit);
 const count=explicit ?? (rows?.reduce((sum,row)=>sum+row.count,0)) ?? fallback;
 if(!Number.isSafeInteger(count)||count<=0)throw new Error('Requested ad count must be a positive whole number.');
 return count;
}
